/**
 * crypto.ts — Low-level cryptographic primitives for Superelectric Ink E2EE.
 *
 * All heavy lifting is done via the Web Crypto API (SubtleCrypto), which is
 * available in every environment this app runs in: browsers, Tauri webview,
 * and Cloudflare Workers.
 *
 * Encoding convention:
 *   Raw bytes <-> base64url strings at API boundaries.
 *   Internally we work with Uint8Array / CryptoKey.
 *
 * Algorithms used:
 *   • AES-256-GCM    — symmetric encryption (doc content, private key, master key)
 *   • HKDF-SHA-256   — key derivation splits from the Argon2id root key
 */

// ── Wire format ───────────────────────────────────────────────────────────────

/**
 * Serializable encrypted blob.
 * `iv` and `ciphertext` are base64url strings so they survive JSON round-trips.
 */
export interface EncryptedBlob {
  /** Base64url-encoded 12-byte AES-GCM IV */
  iv: string
  /** Base64url-encoded ciphertext (includes the 16-byte GCM auth tag) */
  ciphertext: string
}

// ── Base64url helpers ─────────────────────────────────────────────────────────

/** Encode arbitrary bytes as base64url (no padding). */
export function toBase64Url(bytes: Uint8Array): string {
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

/** Decode a base64url string (with or without padding) to Uint8Array. */
export function fromBase64Url(b64: string): Uint8Array {
  // Restore standard base64 padding
  const padded = b64.replace(/-/g, '+').replace(/_/g, '/').padEnd(
    b64.length + ((4 - (b64.length % 4)) % 4),
    '=',
  )
  const binary = atob(padded)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
  return bytes
}

// ── AES-256-GCM ───────────────────────────────────────────────────────────────

/**
 * Import raw bytes as an AES-256-GCM CryptoKey.
 * The key must be exactly 32 bytes (256 bits).
 */
export async function importAesKey(
  rawKey: Uint8Array,
  usages: KeyUsage[] = ['encrypt', 'decrypt'],
  extractable = false,
): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', rawKey as BufferSource, { name: 'AES-GCM', length: 256 }, extractable, usages)
}

/**
 * Export a CryptoKey's raw bytes. Only works for extractable keys.
 */
export async function exportAesKey(key: CryptoKey): Promise<Uint8Array> {
  return new Uint8Array(await crypto.subtle.exportKey('raw', key))
}

/**
 * Generate a fresh, random 256-bit AES-GCM key (extractable so it can be wrapped).
 */
export async function generateAesKey(): Promise<CryptoKey> {
  return crypto.subtle.generateKey(
    { name: 'AES-GCM', length: 256 },
    true, // extractable — needed to wrap/unwrap the key
    ['encrypt', 'decrypt'],
  )
}

/**
 * Encrypt `plaintext` with `key` using AES-256-GCM.
 * A random 12-byte IV is generated for each call.
 * Returns an `EncryptedBlob` suitable for JSON serialization.
 */
export async function aesGcmEncrypt(key: CryptoKey, plaintext: Uint8Array): Promise<EncryptedBlob> {
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const cipherBuf = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    plaintext as BufferSource,
  )
  return {
    iv: toBase64Url(iv),
    ciphertext: toBase64Url(new Uint8Array(cipherBuf)),
  }
}

/**
 * Decrypt an `EncryptedBlob` with `key` using AES-256-GCM.
 */
export async function aesGcmDecrypt(key: CryptoKey, blob: EncryptedBlob): Promise<Uint8Array> {
  const iv = fromBase64Url(blob.iv)
  const ciphertext = fromBase64Url(blob.ciphertext)
  const plainBuf = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: iv as BufferSource },
    key,
    ciphertext as BufferSource,
  )
  return new Uint8Array(plainBuf)
}

// ── HKDF-SHA-256 ──────────────────────────────────────────────────────────────

/**
 * Import raw bytes as an HKDF source key.
 */
async function importHkdfKey(rawKey: Uint8Array): Promise<CryptoKey> {
  return crypto.subtle.importKey('raw', rawKey as BufferSource, 'HKDF', false, ['deriveBits'])
}

/**
 * Derive `lengthBits` of keying material from `ikm` using HKDF-SHA-256.
 *
 * @param ikm         Input keying material (e.g. Argon2id root key bytes)
 * @param info        Context string that domain-separates the output
 * @param lengthBits  Number of bits to derive (default 256)
 * @param salt        Optional salt bytes (defaults to all-zeros per HKDF spec)
 */
export async function hkdfDerive(
  ikm: Uint8Array,
  info: string,
  lengthBits = 256,
  salt?: Uint8Array,
): Promise<Uint8Array> {
  const hkdfKey = await importHkdfKey(ikm)
  const saltBuf = (salt ?? new Uint8Array(32)) as BufferSource
  const infoBuf = new TextEncoder().encode(info) as BufferSource
  const derived = await crypto.subtle.deriveBits(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: saltBuf,
      info: infoBuf,
    },
    hkdfKey,
    lengthBits,
  )
  return new Uint8Array(derived)
}

// ── SHA-256 convenience ───────────────────────────────────────────────────────

/** Return the raw SHA-256 digest bytes of `data`. */
export async function sha256Bytes(data: Uint8Array | string): Promise<Uint8Array> {
  const buf = typeof data === 'string' ? new TextEncoder().encode(data) : data
  const hash = await crypto.subtle.digest('SHA-256', buf as BufferSource)
  return new Uint8Array(hash)
}

// ── Base32 (Crockford) helpers — used for the account recovery key ─────────────
//
// Crockford's alphabet deliberately excludes the visually-ambiguous letters
// I, L, O, and U, which matters here because a recovery key is meant to be
// hand-transcribed or read off a printed page, not just copy-pasted.

const BASE32_ALPHABET = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'

/** Encode arbitrary bytes as an unpadded Crockford base32 string. */
export function bytesToBase32(bytes: Uint8Array): string {
  let bits = 0
  let value = 0
  let output = ''
  for (let i = 0; i < bytes.length; i++) {
    value = (value << 8) | bytes[i]
    bits += 8
    while (bits >= 5) {
      output += BASE32_ALPHABET[(value >>> (bits - 5)) & 31]
      bits -= 5
    }
  }
  if (bits > 0) {
    output += BASE32_ALPHABET[(value << (5 - bits)) & 31]
  }
  return output
}

/** Decode a Crockford base32 string back to bytes. Ignores separators/case. */
export function base32ToBytes(str: string): Uint8Array {
  const clean = str.toUpperCase().replace(/[^0-9A-Z]/g, '')
  let bits = 0
  let value = 0
  const output: number[] = []
  for (let i = 0; i < clean.length; i++) {
    const idx = BASE32_ALPHABET.indexOf(clean[i])
    if (idx === -1) continue
      value = (value << 5) | idx
      bits += 5
      if (bits >= 8) {
        output.push((value >>> (bits - 8)) & 0xff)
        bits -= 8
      }
  }
  return new Uint8Array(output)
}

/** Formats a raw base32 string into dash-separated groups of 4 for display. */
export function formatRecoveryCode(base32: string): string {
  return base32.match(/.{1,4}/g)?.join('-') ?? base32
}

/** Strips whitespace/dashes and normalizes case from a user-entered recovery code. */
export function unformatRecoveryCode(formatted: string): string {
  return formatted.replace(/[\s-]/g, '').toUpperCase()
}
